// ============================================================================
// SYNC WARNING: This file must stay in sync with:
//   Web/Lime/TemplateStaticData.cs
// If you modify this file, update the C# version as well.
// ============================================================================

export interface ElementalSulphurByTexture {
    texture: string
    operatingCec: number
    operator: number
    toPh85: number
    toPh80: number
    toPh75: number
    toPh70: number
}

export interface DepthMultipliers {
    depth15: number
    depth20: number
    depth30: number
    depth40: number
    depth60: number
    depth80: number
    depth100: number
    depth120: number
}

export interface BaseSaturationByDensityStatic {
    operatingDensity: number
    depthMultipliers: DepthMultipliers
}

export interface BaseSaturationByCecStatic {
    cecRange: string
    operatingCec: number
    operator: number
    ca: number
    mg: number
    k: number
    caMgRatio: number
}

// Static data constants (orange/do not edit in Excel)
export const ELEMENT_SPECIFIC_GRAVITY = {
    Ca: 200,
    Mg: 122,
    K: 391,
    Na: 230
}

export const ELEMENTAL_SULPHUR_BY_TEXTURE: ElementalSulphurByTexture[] = [
    { texture: 'Sand', operatingCec: 0.1, operator: 1, toPh85: 1831.31, toPh80: 1765.12, toPh75: 1676.86, toPh70: 1412.09 },
    { texture: 'Loamy Sand', operatingCec: 6, operator: 2, toPh85: 2441.75, toPh80: 2353.50, toPh75: 2243.18, toPh70: 1890.15 },
    { texture: 'Sandy Loam', operatingCec: 9, operator: 3, toPh85: 3052.20, toPh80: 2941.88, toPh75: 2809.49, toPh70: 2368.21 },
    { texture: 'Loam', operatingCec: 12, operator: 4, toPh85: 3662.64, toPh80: 3530.26, toPh75: 3375.81, toPh70: 2846.28 },
    { texture: 'Clay Loam', operatingCec: 18, operator: 5, toPh85: 4876.16, toPh80: 4699.65, toPh75: 4493.73, toPh70: 3802.31 },
    { texture: 'Sand Clay', operatingCec: 24, operator: 6, toPh85: 6089.68, toPh80: 5869.04, toPh75: 5611.64, toPh70: 4758.34 },
    { texture: 'Clay', operatingCec: 30, operator: 7, toPh85: 7303.20, toPh80: 7038.43, toPh75: 6729.56, toPh70: 5714.37 }
]

export const BASE_SATURATION_BY_DENSITY_STATIC: BaseSaturationByDensityStatic[] = [
    { operatingDensity: 0, depthMultipliers: { depth15: 1.65, depth20: 2.20, depth30: 3.30, depth40: 4.40, depth60: 6.60, depth80: 8.80, depth100: 11.00, depth120: 13.20 } },
    { operatingDensity: 1.10, depthMultipliers: { depth15: 1.65, depth20: 2.20, depth30: 3.30, depth40: 4.40, depth60: 6.60, depth80: 8.80, depth100: 11.00, depth120: 13.20 } },
    { operatingDensity: 1.15, depthMultipliers: { depth15: 1.725, depth20: 2.30, depth30: 3.45, depth40: 4.60, depth60: 6.90, depth80: 9.20, depth100: 11.50, depth120: 13.80 } },
    { operatingDensity: 1.20, depthMultipliers: { depth15: 1.80, depth20: 2.40, depth30: 3.60, depth40: 4.80, depth60: 7.20, depth80: 9.60, depth100: 12.00, depth120: 14.40 } },
    { operatingDensity: 1.25, depthMultipliers: { depth15: 1.875, depth20: 2.50, depth30: 3.75, depth40: 5.00, depth60: 7.50, depth80: 10.00, depth100: 12.50, depth120: 15.00 } },
    { operatingDensity: 1.30, depthMultipliers: { depth15: 1.95, depth20: 2.60, depth30: 3.90, depth40: 5.20, depth60: 7.80, depth80: 10.40, depth100: 13.00, depth120: 15.60 } },
    { operatingDensity: 1.35, depthMultipliers: { depth15: 2.025, depth20: 2.70, depth30: 4.05, depth40: 5.40, depth60: 8.10, depth80: 10.80, depth100: 13.50, depth120: 16.20 } },
    { operatingDensity: 1.40, depthMultipliers: { depth15: 2.10, depth20: 2.80, depth30: 4.20, depth40: 5.60, depth60: 8.40, depth80: 11.20, depth100: 14.00, depth120: 16.80 } },
    { operatingDensity: 1.45, depthMultipliers: { depth15: 2.175, depth20: 2.90, depth30: 4.35, depth40: 5.80, depth60: 8.70, depth80: 11.60, depth100: 14.50, depth120: 17.40 } }
]

export const BASE_SATURATION_BY_CEC_STATIC: BaseSaturationByCecStatic[] = [
    { cecRange: 'x < 1', operatingCec: 0, operator: 1, ca: 0.65, mg: 0.23, k: 0.07, caMgRatio: 2.8261 },
    { cecRange: '1 < x < 2', operatingCec: 1, operator: 2, ca: 0.6538, mg: 0.2231, k: 0.068, caMgRatio: 2.9305 },
    { cecRange: '2 < x < 4', operatingCec: 2, operator: 3, ca: 0.6576, mg: 0.2162, k: 0.066, caMgRatio: 3.0416 },
    { cecRange: '4 < x < 6', operatingCec: 4, operator: 4, ca: 0.6614, mg: 0.2093, k: 0.064, caMgRatio: 3.1601 },
    { cecRange: '6 < x < 8', operatingCec: 6, operator: 5, ca: 0.6652, mg: 0.2024, k: 0.062, caMgRatio: 3.2866 },
    { cecRange: '8 < x < 10', operatingCec: 8, operator: 6, ca: 0.6690, mg: 0.1955, k: 0.06, caMgRatio: 3.4220 },
    { cecRange: '10 < x < 12', operatingCec: 10, operator: 7, ca: 0.6728, mg: 0.1886, k: 0.058, caMgRatio: 3.5673 },
    { cecRange: '12 < x < 14', operatingCec: 12, operator: 8, ca: 0.6766, mg: 0.1817, k: 0.056, caMgRatio: 3.7237 },
    { cecRange: '14 < x < 16', operatingCec: 14, operator: 9, ca: 0.6804, mg: 0.1748, k: 0.054, caMgRatio: 3.8924 },
    { cecRange: '16 < x < 18', operatingCec: 16, operator: 10, ca: 0.6842, mg: 0.1679, k: 0.052, caMgRatio: 4.0750 },
    { cecRange: '18 < x < 20', operatingCec: 18, operator: 11, ca: 0.6880, mg: 0.1610, k: 0.05, caMgRatio: 4.2733 },
    { cecRange: '20 < x < 22', operatingCec: 20, operator: 12, ca: 0.6918, mg: 0.1541, k: 0.048, caMgRatio: 4.4893 },
    { cecRange: 'x > 22', operatingCec: 22, operator: 13, ca: 0.6956, mg: 0.1472, k: 0.046, caMgRatio: 4.7255 }
]

// Product source options for lime programs
export const POTASSIUM_SOURCES = ['K2SO4', 'KCl', 'MOP', 'SOP', 'KTS', 'KNO3', 'K-mag'] as const
export const PHOSPHATE_SOURCES = ['MAP', 'DAP', 'TSP', 'SSP', 'Rock Phosphate'] as const
